import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DivergenciaDataDialog from "@/components/DivergenciaDataDialog";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { stageLabel } from "@/lib/format";
import { trancarPagina } from "@/lib/lockScroll";
import { extractDateFromText, isSameLocalDay } from "@/lib/dateFromText";
import { Building2, KanbanSquare, MapPin, Phone, Mail, Video, Loader2, ArrowRight, X, ArrowDown } from "@/components/icons";
import { MessageCircle as WhatsIcon } from "@/components/icons";
import { LINHAS_MIX } from "@/constants/linhasMix";

const INPUT = "w-full min-w-0 h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition";
const LABEL = "text-xs font-semibold uppercase tracking-wider text-muted-foreground";

// Canal do que aconteceu — item 2 do PEDIDO_ e correção do LAYOUT:
// texto curto que cabe numa linha, "Visita" continua gravando como
// visita presencial no histórico (só o label mudou).
const CANAIS = [
  { k: "Visita",       label: "Visita",    Icon: MapPin },
  { k: "Ligação",      label: "Ligação",   Icon: Phone },
  { k: "WhatsApp",     label: "WhatsApp",  Icon: WhatsIcon },
  { k: "E-mail",       label: "E-mail",    Icon: Mail },
  { k: "Videochamada", label: "Vídeo",     Icon: Video },
];

const TIPOS_PROX = [
  "Visita", "Ligação", "WhatsApp", "E-mail", "Reunião",
  "Teste/Demonstração", "Enviar proposta", "Fechamento",
];

const OPEN_STAGES = ["Prospecção", "Qualificação", "Testes", "Proposta", "Negociação"];

const TIPO_TO_CANAL = {
  "Visita": "Visita", "Ligação": "Ligação", "WhatsApp": "WhatsApp",
  "E-mail": "E-mail", "Reunião": "Videochamada",
  "Teste/Demonstração": "Visita", "Enviar proposta": "E-mail",
  "Próximo contato": "Ligação", "Retorno": "Ligação",
};

// "O que foi feito" — pedido 2B-1. Escolha única obrigatória. Controla o
// avanço automático da etapa (Contato/Visita técnica → Qualificação,
// Teste → Testes, Proposta enviada → Proposta, Negociação → Negociação).
const OQ_FEITO = [
  "Contato",
  "Visita técnica",
  "Teste / demonstração",
  "Proposta enviada",
  "Negociação",
];
const OQ_STAGE = {
  "Contato": "Qualificação",
  "Visita técnica": "Qualificação",
  "Teste / demonstração": "Testes",
  "Proposta enviada": "Proposta",
  "Negociação": "Negociação",
};
const TIPO_TO_OQ = {
  "Visita": "Visita técnica",
  "Teste/Demonstração": "Teste / demonstração",
  "Enviar proposta": "Proposta enviada",
};
const STAGE_IDX = { "Prospecção": 0, "Qualificação": 1, "Testes": 2, "Proposta": 3, "Negociação": 4 };

const nowLocalInput = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const nextBusinessDayAt9 = () => {
  const d = new Date(); d.setHours(9, 0, 0, 0); d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const toLocal = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return nowLocalInput();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function RegistrarAtividade({
  open,
  onOpenChange,
  cliente,
  deal,
  carteira,
  concluir,
  onSaved,
  // LOTE V-1a item 2: quando `true`, ao abrir a janela já rola o conteúdo
  // até o bloco "Próximo passo" (ra-prox) em vez de abrir no topo. Usado
  // pelo card "Próxima ação" quando não há próxima ação definida.
  focusProx,
  // LOTE F1 — ao abrir pelo atalho de um requisito ("Registrar Proposta
  // enviada" etc.), pré-seleciona o "O que foi feito" correspondente para
  // que o texto da etapa seguinte fique certo.
  oqInicial,
}) {
  const [clienteId, setClienteId] = useState(cliente?.id || "");
  const [oportunidades, setOportunidades] = useState([]);
  const [dealId, setDealId] = useState(deal?.id || "");
  const [canal, setCanal] = useState(concluir ? (TIPO_TO_CANAL[concluir.tipo] || "Ligação") : "Visita");
  const [data, setData] = useState(concluir?.data ? toLocal(concluir.data) : nowLocalInput());
  const [obs, setObs] = useState("");
  const [proxTipo, setProxTipo] = useState(TIPOS_PROX[0]);
  // LOTE AG-1: data da próxima nasce VAZIA (antes vinha preenchida com
  // "próximo dia útil às 09:00", o que fazia a maioria dos vendedores
  // esquecer de ajustar e gerar atividades "atrasadas" no dia seguinte).
  const [proxData, setProxData] = useState("");
  const [proxDesc, setProxDesc] = useState("");
  // "O que foi feito" — pedido 2B item 1. Padrão vem do tipo da atividade
  // pendente ("Concluir"); senão, "Contato".
  const [oq, setOq] = useState(oqInicial || TIPO_TO_OQ[concluir?.tipo] || "Contato");
  const [busy, setBusy] = useState(false);
  // LOTE UX-1 A6c — chips "Linhas oferecidas" aparecem quando a oportunidade
  // é de ampliação de mix (tipo="ampliacao_mix" OU cross_selling=true).
  const [linhasOf, setLinhasOf] = useState([]);
  // Divergência de data: data escrita no texto do próximo passo ≠ data do campo.
  // Guardamos o payload pendente até o usuário escolher qual data manter.
  const [divergencia, setDivergencia] = useState(null);
  const painelRef = useRef(null);
  // LOTE UX-1 A6b — referência ao bloco "Próximo passo" para rolar no mobile.
  const proxRef = useRef(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setClienteId(cliente?.id || "");
    setDealId(deal?.id || "");
    setCanal(concluir ? (TIPO_TO_CANAL[concluir.tipo] || "Ligação") : "Visita");
    setData(concluir?.data ? toLocal(concluir.data) : nowLocalInput());
    setObs(concluir?.titulo || "");
    setProxTipo(TIPOS_PROX[0]);
    setProxData("");
    setProxDesc("");
    setOq(oqInicial || TIPO_TO_OQ[concluir?.tipo] || "Contato");
    setLinhasOf([]);
  }, [open, cliente?.id, deal?.id, concluir?.id, concluir?.data, concluir?.tipo, concluir?.titulo, oqInicial]);

  // Trava rolagem do fundo + fecha com Esc — mesmo padrão do Resumo/MetaBatida.
  useEffect(() => {
    if (!open) return;
    const liberar = trancarPagina();
    const onKey = (e) => { if (e.key === "Escape") onOpenChange?.(false); };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); liberar(); };
  }, [open, onOpenChange]);

  // LOTE V-1a item 2: ao abrir pelo card "Próxima ação" sem próxima definida,
  // rola o scroller até o bloco ra-prox (celular e computador). Esperamos um
  // frame para o bloco já estar montado.
  useEffect(() => {
    if (!open || !focusProx) return;
    const id = requestAnimationFrame(() => {
      try {
        proxRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
      } catch { /* ignore */ }
    });
    return () => cancelAnimationFrame(id);
  }, [open, focusProx, deal?.id]);

  useEffect(() => {
    if (!open || deal?.id) { setOportunidades([]); return; }
    const cid = clienteId || cliente?.id;
    if (!cid) { setOportunidades([]); return; }
    api.get("/deals").then((r) => {
      const abertas = (r.data || []).filter((d) => d.cliente_id === cid && OPEN_STAGES.includes(d.stage));
      setOportunidades(abertas);
      if (abertas.length === 1) setDealId(abertas[0].id);
      else if (abertas.length === 0) setDealId("");
    }).catch(() => setOportunidades([]));
  }, [open, clienteId, cliente?.id, deal?.id]);

  const dealSelecionada = useMemo(() => {
    if (deal) return deal;
    return oportunidades.find((d) => d.id === dealId) || null;
  }, [deal, oportunidades, dealId]);

  const proximaEtapa = useMemo(() => {
    const s = dealSelecionada?.stage;
    const i = OPEN_STAGES.indexOf(s);
    return i >= 0 && i < OPEN_STAGES.length - 1 ? OPEN_STAGES[i + 1] : null;
  }, [dealSelecionada]);

  const temDealAberta = !!dealSelecionada && OPEN_STAGES.includes(dealSelecionada.stage);

  // LOTE UX-1 A6c — flag "é ampliação de mix" (tipo explícito OU etiqueta).
  const ehAmpliacao = !!(dealSelecionada && (dealSelecionada.tipo === "ampliacao_mix" || dealSelecionada.cross_selling === true));

  const toggleLinha = (l) => setLinhasOf((s) => (s.includes(l) ? s.filter((x) => x !== l) : [...s, l]));

  const precisaPular = useMemo(() => {
    if (!dealSelecionada) return false;
    const alvoIdx = STAGE_IDX[OQ_STAGE[oq]];
    const atualIdx = STAGE_IDX[dealSelecionada.stage];
    if (alvoIdx == null || atualIdx == null) return false;
    return alvoIdx > atualIdx + 1;
  }, [dealSelecionada, oq]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const cid = cliente?.id || clienteId;
    if (!cid) { toast.error("Escolha o cliente."); return; }
    if (!obs.trim()) { toast.error("Descreva o que foi conversado."); return; }
    const dt = new Date(data);
    if (Number.isNaN(dt.getTime())) { toast.error("Data inválida."); return; }
    if (dt.getTime() > Date.now() + 60 * 1000) { toast.error("Para algo futuro, agende como próximo passo."); return; }
    if (temDealAberta) {
      if (!proxDesc.trim()) {
        // LOTE UX-1 A6b — rola até o bloco e avisa.
        proxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        toast.error("Descreva o próximo passo.");
        return;
      }
      if (!proxData) {
        proxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        toast.error("Escolha a data e a hora da próxima ação.");
        return;
      }
      const dp = new Date(proxData);
      if (Number.isNaN(dp.getTime()) || dp.getTime() < Date.now() - 60 * 1000) {
        proxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        toast.error("O próximo passo precisa ficar no futuro."); return;
      }
    }
    if (precisaPular && !dealSelecionada) { /* seguro */ }

    // Aviso de divergência: data no texto do próximo passo ≠ data do campo.
    // Só quando há deal aberta (próximo passo ativo) e o texto cita uma data.
    if (temDealAberta) {
      const dTexto = extractDateFromText(proxDesc);
      const dCampo = new Date(proxData);
      if (dTexto && !isSameLocalDay(dTexto, dCampo)) {
        setDivergencia({ texto: dTexto, campo: dCampo });
        return; // interrompe — _executarSalvar é chamado após escolha do usuário.
      }
    }
    await _executarSalvar(new Date(proxData));
  };

  const _executarSalvar = async (dataProx) => {
    const cid = cliente?.id || clienteId;
    const dt = new Date(data);
    setBusy(true);
    try {
      const clienteNome = cliente?.nome || cliente?.empresa
        || (carteira || []).find((c) => c.id === cid)?.nome
        || "";
      const resp = await api.post("/atividades", {
        titulo: obs.trim().slice(0, 120),
        tipo: canal,
        data: dt.toISOString(),
        cliente_id: cid,
        cliente_nome: clienteNome,
        deal_id: dealSelecionada?.id || null,
        deal_titulo: dealSelecionada?.titulo || "",
        descricao: obs.trim(),
        concluida: true,
        o_que_foi_feito: oq,
        // LOTE UX-1 A6c — linhas de mix só são gravadas quando a deal é
        // de ampliação (tipo explícito ou cross_selling marcado).
        linhas_oferecidas: ehAmpliacao && linhasOf.length ? linhasOf : null,
      });
      if (concluir?.id) {
        try { await api.patch(`/atividades/${concluir.id}/toggle`); } catch { /* silencioso */ }
      }
      if (temDealAberta) {
        await api.post("/atividades", {
          titulo: proxDesc.trim().slice(0, 120),
          tipo: proxTipo,
          data: dataProx.toISOString(),
          cliente_id: cid,
          cliente_nome: clienteNome,
          deal_id: dealSelecionada.id,
          deal_titulo: dealSelecionada.titulo || "",
          descricao: proxDesc.trim(),
        });
      }
      // Trata o avanço automático devolvido pelo backend.
      const av = resp?.data?._avanco;
      if (av?.advanced) toast.success(`Avançou para ${av.to_stage}.`);
      else if (av?.motivo === "prospeccao_incompleta") {
        toast.warning("A oportunidade não avançou. Faltam: " + (av.faltando || []).join(", ") + ".");
      } else if (av?.motivo === "usar_oportunidade") {
        toast.warning("Para pular etapa, use a oportunidade.");
      }
      toast.success("Atividade registrada");
      onOpenChange?.(false);
      onSaved?.();
      setDivergencia(null);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  // Layout do dialog — ver CORRECAO_EMERGENT_REGISTRAR_ATIVIDADE_LAYOUT.md.
  // Computador: caixa centralizada 640px, título/rodapé fixos, meio scrolla se
  // não couber. Celular (<560px): bottom-sheet 94dvh, cantos só em cima.
  const ui = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ra-titulo"
      data-testid="registrar-atividade-dialog"
      className="fixed inset-0 z-[1000] flex justify-center items-end sm:items-center overflow-hidden"
      style={{ height: "100dvh", touchAction: "none" }}
      onClick={(e) => { if (e.target === e.currentTarget) onOpenChange?.(false); }}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        style={{ overscrollBehavior: "contain", touchAction: "none" }}
      />
      <div
        ref={painelRef}
        className="relative bg-white shadow-2xl flex flex-col ra-painel"
        style={{
          width: "min(640px, 100vw - 32px)",
          maxHeight: "calc(100dvh - 48px)",
          borderRadius: 16,
          overflow: "hidden",
        }}
      >
        {/* Título fixo — faixa azul-marinho padrão (AppModal) */}
        <header
          className="flex items-center justify-between px-5 sm:px-6 py-4"
          style={{ background: "#003E62", color: "#FFFFFF" }}
        >
          <h2 id="ra-titulo" className="text-base font-semibold" style={{ color: "#FFFFFF" }}>Registrar atividade</h2>
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            aria-label="Fechar"
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: "rgba(255,255,255,.08)", color: "#FFFFFF" }}
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <form onSubmit={submit} data-testid="registrar-atividade-form" className="flex-1 flex flex-col min-h-0">
          {/* Corpo — meio rolável quando não couber */}
          <div ref={scrollerRef} className="ra-corpo flex-1 overflow-y-auto overflow-x-hidden px-5 sm:px-6 py-4">
            {/* LOTE UX-1 A6b — no mobile, aviso fixo "Role para definir o
                próximo passo ↓" aparece quando há deal aberta e o bloco
                ainda não foi tocado. */}
            {temDealAberta && (
              <div
                className="sm:hidden mb-3 rounded-lg bg-brand-soft/60 border border-brand/20 px-3 py-2 flex items-center justify-between gap-2 text-xs text-brand-hover"
                data-testid="ra-mobile-hint"
              >
                <span className="font-medium">Role para definir o próximo passo</span>
                <button type="button" onClick={() => proxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
                  className="shrink-0 flex items-center gap-1 font-semibold">
                  <ArrowDown className="w-3 h-3" /> Ir
                </button>
              </div>
            )}
            {/* Linha 1: Cliente | Oportunidade */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <span className={LABEL}>Cliente</span>
                {cliente ? (
                  <div
                    data-testid="ra-cliente-lock"
                    className="mt-1 flex items-center gap-2 h-11 px-3.5 rounded-lg border border-input bg-slate-50 text-sm text-navy min-w-0"
                  >
                    <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="truncate">{cliente.empresa || cliente.nome}</span>
                  </div>
                ) : (
                  <select
                    value={clienteId}
                    onChange={(e) => { setClienteId(e.target.value); setDealId(""); }}
                    className={INPUT + " mt-1"}
                    data-testid="ra-cliente"
                    required
                  >
                    <option value="">— Selecione um cliente —</option>
                    {(carteira || []).map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}{c.empresa ? ` · ${c.empresa}` : ""}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="min-w-0">
                <span className={LABEL}>Oportunidade</span>
                {deal ? (
                  <div
                    data-testid="ra-deal-lock"
                    className="mt-1 flex items-center gap-2 h-11 px-3.5 rounded-lg border border-input bg-slate-50 text-sm text-navy min-w-0"
                  >
                    <KanbanSquare className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="truncate">{deal.titulo || "Oportunidade"} · {stageLabel(deal.stage)}</span>
                  </div>
                ) : oportunidades.length === 0 ? (
                  <div className="mt-1 h-11 px-3.5 rounded-lg border border-dashed border-input flex items-center text-xs text-muted-foreground min-w-0">
                    {clienteId ? "Sem oportunidade aberta — só relacionamento." : "Escolha o cliente primeiro."}
                  </div>
                ) : (
                  <select
                    value={dealId}
                    onChange={(e) => setDealId(e.target.value)}
                    className={INPUT + " mt-1"}
                    data-testid="ra-deal"
                  >
                    <option value="">Sem oportunidade (só relacionamento)</option>
                    {oportunidades.map((d) => (
                      <option key={d.id} value={d.id}>{d.titulo || "Oportunidade"} · {stageLabel(d.stage)}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Linha 2: 5 canais lado a lado (>=sm) / 3 por linha no celular */}
            <div className="mt-4">
              <span className={LABEL}>Como foi</span>
              <div className="mt-1 grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="ra-canal">
                {CANAIS.map((c) => {
                  const active = canal === c.k;
                  return (
                    <button
                      key={c.k}
                      type="button"
                      onClick={() => setCanal(c.k)}
                      data-testid={`ra-canal-${c.k}`}
                      className={`h-11 min-w-0 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 px-2 whitespace-nowrap transition-colors ${active ? "bg-brand text-white border-brand" : "border-input text-navy hover:bg-slate-50"}`}
                    >
                      <c.Icon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Linha 2b: O que foi feito (item 1 do pedido 2B) */}
            {/* LOTE F1 — quando aberto pelo atalho de um requisito, "O que foi
                feito" já vem definido e vai para "Mais opções" (abre só se o
                vendedor quiser mudar). Senão fica visível, como antes. */}
            {oqInicial ? (
              <details className="mt-4" data-testid="ra-oq-mais">
                <summary className="cursor-pointer text-xs font-semibold text-brand hover:underline list-none">
                  Mais opções (O que foi feito: <span className="text-navy">{oq}</span> · próxima etapa: {OQ_STAGE[oq]})
                </summary>
                <div className="mt-2">
                  <span className={LABEL}>O que foi feito</span>
                  <div className="mt-1 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="ra-oq-feito">
                    {OQ_FEITO.map((o) => {
                      const active = oq === o;
                      return (
                        <button key={o} type="button" onClick={() => setOq(o)} data-testid={`ra-oq-${o}`}
                          className={`h-11 min-w-0 rounded-lg border text-xs sm:text-[13px] font-medium flex items-center justify-center px-2 text-center leading-tight transition-colors ${active ? "bg-brand text-white border-brand" : "border-input text-navy hover:bg-slate-50"}`}>
                          <span>{o}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </details>
            ) : (
              <div className="mt-4">
                <span className={LABEL}>O que foi feito</span>
                <div className="mt-1 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="ra-oq-feito">
                  {OQ_FEITO.map((o) => {
                    const active = oq === o;
                    return (
                      <button key={o} type="button" onClick={() => setOq(o)} data-testid={`ra-oq-${o}`}
                        className={`h-11 min-w-0 rounded-lg border text-xs sm:text-[13px] font-medium flex items-center justify-center px-2 text-center leading-tight transition-colors ${active ? "bg-brand text-white border-brand" : "border-input text-navy hover:bg-slate-50"}`}>
                        <span>{o}</span>
                      </button>
                    );
                  })}
                </div>
                {dealSelecionada && !precisaPular && (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Este registro empurra a oportunidade para{" "}
                    <span className="font-semibold text-navy">{OQ_STAGE[oq]}</span>{" "}
                    quando os requisitos forem atendidos.
                  </p>
                )}
              </div>
            )}

            {/* Linha 3: Quando aconteceu | Etapa atual (indicação) */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <label className={LABEL}>Quando aconteceu</label>
                <input
                  type="datetime-local"
                  value={data}
                  max={nowLocalInput()}
                  onChange={(e) => setData(e.target.value)}
                  className={INPUT + " mt-1"}
                  data-testid="ra-data"
                  required
                />
              </div>
              {dealSelecionada ? (
                <div className="min-w-0">
                  <span className={LABEL}>Etapa atual</span>
                  <div className="mt-1 h-11 px-3.5 rounded-lg border border-input bg-slate-50 text-sm text-navy flex items-center">
                    {stageLabel(dealSelecionada.stage)}
                  </div>
                </div>
              ) : (
                <div aria-hidden className="hidden sm:block" />
              )}
            </div>

            {/* Aviso 2B-1: registro de etapa muito adiante da atual não avança. */}
            {precisaPular && (
              <div className="mt-4 rounded-xl border border-warn/40 bg-warn-soft p-3 min-w-0 text-sm text-navy" data-testid="ra-aviso-pular">
                Registrar <b>{oq}</b> em <b>{stageLabel(dealSelecionada?.stage)}</b> não avança a oportunidade
                (ficaria mais de uma etapa à frente). Para pular etapa, use a oportunidade.
              </div>
            )}

            {/* Linha 4: O que foi conversado (largura total, 3 linhas) */}
            <div className="mt-4 min-w-0">
              <label className={LABEL}>O que foi conversado</label>
              <textarea
                value={obs}
                onChange={(e) => setObs(e.target.value)}
                rows={3}
                placeholder="Ex.: Fizemos o levantamento do consumo mensal. A Fabiana pediu proposta da linha hospitalar."
                className={`${INPUT} mt-1 h-auto py-2.5`}
                data-testid="ra-obs"
                required
              />
            </div>

            {/* Linha 5: Próximo passo (Tipo | Data e hora; Descrição embaixo) */}
            {temDealAberta && (
              <div ref={proxRef} className="mt-4 rounded-xl border border-dashed border-brand/40 bg-brand-soft/40 p-4 min-w-0" data-testid="ra-prox">
                <div>
                  <h3 className="text-sm font-semibold text-navy">Próximo passo</h3>
                  <p className="text-[11px] text-muted-foreground">Obrigatório quando há oportunidade aberta. Vai para a Agenda e para o card &quot;Próxima ação&quot;.</p>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <label className={LABEL}>Tipo</label>
                    <select value={proxTipo} onChange={(e) => setProxTipo(e.target.value)} className={INPUT + " mt-1"} data-testid="ra-prox-tipo">
                      {TIPOS_PROX.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="min-w-0">
                    <label className={LABEL}>Data e hora</label>
                    {/* LOTE AG-1: atalhos opcionais Hoje · Amanhã para preencher
                        com um toque, ainda assim deixando o vendedor conferir
                        no input abaixo antes de salvar. */}
                    <div className="flex gap-1.5 mt-1">
                      <button type="button" onClick={() => {
                        const d = new Date(); d.setHours(9, 0, 0, 0);
                        const pad = (n) => String(n).padStart(2, "0");
                        setProxData(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                      }} data-testid="ra-prox-hoje" className="text-xs px-2 py-1 rounded-md border border-input bg-white text-slate-600 hover:bg-brand-soft hover:text-brand">Hoje 09h</button>
                      <button type="button" onClick={() => setProxData(nextBusinessDayAt9())}
                        data-testid="ra-prox-amanha" className="text-xs px-2 py-1 rounded-md border border-input bg-white text-slate-600 hover:bg-brand-soft hover:text-brand">Próximo dia útil 09h</button>
                    </div>
                    <input
                      type="datetime-local"
                      min={nowLocalInput()}
                      value={proxData}
                      onChange={(e) => setProxData(e.target.value)}
                      className={INPUT + " mt-2"}
                      data-testid="ra-prox-data"
                      required
                    />
                  </div>
                </div>
                <div className="mt-3 min-w-0">
                  <label className={LABEL}>Descrição curta</label>
                  <input
                    value={proxDesc}
                    onChange={(e) => setProxDesc(e.target.value)}
                    maxLength={120}
                    placeholder="Ex.: Enviar proposta da linha hospitalar até 6ª-feira"
                    className={INPUT + " mt-1"}
                    data-testid="ra-prox-desc"
                    required
                  />
                </div>
              </div>
            )}

            {/* LOTE UX-1 A6c — Linhas oferecidas quando é ampliação de mix. */}
            {ehAmpliacao && (
              <div className="mt-4 rounded-xl border border-brand/30 bg-brand-soft/30 p-4 min-w-0" data-testid="ra-linhas-mix">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <h3 className="text-sm font-semibold text-navy">Linhas oferecidas</h3>
                  <span className="text-[11px] text-muted-foreground">(opcional) o que você apresentou nessa atividade</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {LINHAS_MIX.map((l) => {
                    const on = linhasOf.includes(l);
                    return (
                      <button
                        key={l}
                        type="button"
                        onClick={() => toggleLinha(l)}
                        data-testid={`ra-linha-${l}`}
                        className={`h-9 px-3 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-input hover:bg-brand-soft hover:text-brand"}`}
                      >
                        {l}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Rodapé fixo — padrão AppModal (bg #FAFCFD, borda #EEF2F5) */}
          <footer
            className="flex items-center justify-end gap-3 px-5 sm:px-6 py-4 flex-wrap"
            style={{
              background: "#FAFCFD",
              borderTop: "1px solid #EEF2F5",
              paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
            }}
          >
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              className="h-[38px] min-h-[44px] sm:min-h-[38px] px-4 rounded-lg bg-white border border-[#D5DEE5] text-sm font-semibold text-navy hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="ra-save"
              className="h-[38px] min-h-[44px] sm:min-h-[38px] px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover flex items-center gap-2 disabled:opacity-60"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
            </button>
          </footer>
        </form>

        <DivergenciaDataDialog
          open={!!divergencia}
          onClose={() => setDivergencia(null)}
          dataTexto={divergencia?.texto}
          dataCampo={divergencia?.campo}
          testId="div-data-dialog"
          onManter={() => { setDivergencia(null); _executarSalvar(new Date(proxData)); }}
          onUsarTexto={() => {
            // Mantém a HORA do campo, só troca o DIA pelo da descrição.
            const base = new Date(proxData);
            const nova = new Date(
              divergencia.texto.getFullYear(),
              divergencia.texto.getMonth(),
              divergencia.texto.getDate(),
              base.getHours(),
              base.getMinutes(),
              0, 0
            );
            const yyyy = nova.getFullYear();
            const mm = String(nova.getMonth() + 1).padStart(2, "0");
            const dd = String(nova.getDate()).padStart(2, "0");
            const hh = String(nova.getHours()).padStart(2, "0");
            const mi = String(nova.getMinutes()).padStart(2, "0");
            setProxData(`${yyyy}-${mm}-${dd}T${hh}:${mi}`);
            setDivergencia(null);
            _executarSalvar(nova);
          }}
        />
      </div>
    </div>
  );

  if (typeof document === "undefined") return ui;
  return createPortal(ui, document.body);
}
